import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import solutionContent from '../data/solutionContent';

const categoryHeaders = {
  'private-equity': '/header_alternative.png', 'private-credit': '/header_alternative.png',
  'real-assets': '/header_alternative.png', 'hedge-funds': '/header_alternative.png',
  'digital-assets': '/header_digital.png', 'forex': '/header_traditional.png',
  'hard-commodities': '/header_traditional.png', 'soft-commodities': '/header_traditional.png',
  'fixed-income': '/header_traditional.png', 'collectibles': '/header_specialty.png',
  'structured-products': '/header_specialty.png', 'environmental-assets': '/header_specialty.png'
};

const SolutionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const sol = solutionContent[id];

  const fadeUp = (delay = 0) => ({
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: '-80px' },
    transition: { duration: 0.6, delay }
  });

  if (!sol) {
    return (
      <div style={{ paddingTop: '12rem', textAlign: 'center', minHeight: '60vh' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: '800', marginBottom: '1rem' }}>Solution Not Found</h1>
        <p style={{ color: 'var(--text-secondary)', marginBottom: '2rem' }}>The asset class you're looking for doesn't exist.</p>
        <button onClick={() => navigate('/solutions')} style={{ background: 'var(--accent-gradient)', padding: '1rem 2rem', borderRadius: '12px', border: 'none', color: '#000', fontWeight: '700', cursor: 'pointer' }}>View All Solutions</button>
      </div>
    );
  }

  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'strategies', label: 'Investment Strategies' },
    { key: 'risk', label: 'Risk Management' },
    { key: 'advantage', label: 'Institutional Advantage' }
  ];

  const profile = sol.investmentProfile;
  const headerImg = categoryHeaders[id] || '/header_alternative.png';

  return (
    <div style={{ paddingTop: '5rem', color: 'var(--text-primary)' }}>
      {/* Hero Banner with Image */}
      <div style={{ position: 'relative', height: '280px', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(${headerImg})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.4), rgba(0,0,0,0.85))' }} />
        <div style={{ position: 'relative', zIndex: 1, maxWidth: '1000px', margin: '0 auto', padding: '0 5%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', paddingBottom: '2rem' }}>
          <motion.div {...fadeUp()}>
            <button onClick={() => navigate('/solutions')} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', fontWeight: '600', fontSize: '0.85rem', marginBottom: '1.5rem', padding: 0 }}>
              <ArrowLeft size={16} /> Back to Solutions
            </button>
          </motion.div>
          <motion.div {...fadeUp(0.05)} style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.8rem' }}>
            <span style={{ fontSize: '2.5rem' }}>{sol.emoji}</span>
            <div>
              <p style={{ color: 'var(--accent-primary)', fontWeight: '700', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.1em', margin: 0 }}>{sol.subtitle}</p>
              <h1 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: '800', margin: 0, letterSpacing: '-0.02em', color: '#fff' }}>{sol.title}</h1>
            </div>
          </motion.div>
          <motion.p {...fadeUp(0.1)} style={{ color: 'rgba(255,255,255,0.85)', fontSize: '1rem', lineHeight: '1.6', maxWidth: '750px' }}>{sol.desc.length > 200 ? sol.desc.substring(0, 200) + '...' : sol.desc}</motion.p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div style={{ borderBottom: '1px solid var(--border-color)', position: 'sticky', top: 'var(--topbar-height)', background: 'var(--bg-primary)', zIndex: 50, backdropFilter: 'blur(10px)' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto', display: 'flex', gap: '0', overflowX: 'auto', padding: '0 5%' }}>
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                padding: '1rem 1.5rem', background: 'none', border: 'none', cursor: 'pointer',
                fontWeight: '700', fontSize: '0.9rem', whiteSpace: 'nowrap', transition: 'all 0.2s',
                color: activeTab === tab.key ? 'var(--accent-primary)' : 'var(--text-secondary)',
                borderBottom: activeTab === tab.key ? '2px solid var(--accent-primary)' : '2px solid transparent'
              }}
            >{tab.label}</button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <motion.div key={activeTab} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>

        {/* ===== OVERVIEW TAB ===== */}
        {activeTab === 'overview' && (
          <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '3rem 5%' }}>
            {/* Investment Profile Table */}
            <motion.div {...fadeUp()} style={{ marginBottom: '4rem' }}>
              <h2 style={{ fontSize: '1.8rem', fontWeight: '800', marginBottom: '1.5rem' }}>Investment Profile</h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '1rem' }}>
                {[
                  { label: 'Risk Level', value: profile.risk },
                  { label: 'Liquidity', value: profile.liquidity },
                  { label: 'Time Horizon', value: profile.horizon },
                  { label: 'Target Yield', value: profile.targetYield }
                ].map((item, i) => (
                  <div key={i} style={{ background: 'var(--bg-secondary)', padding: '1rem', borderRadius: '16px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 0.5rem' }}>{item.label}</p>
                    <p style={{ fontSize: '1.1rem', fontWeight: '800', margin: 0, color: 'var(--accent-primary)' }}>{item.value}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Market Thesis */}
            <motion.div {...fadeUp(0.1)} style={{ marginBottom: '4rem' }}>
              <h2 style={{ fontSize: '1.8rem', fontWeight: '800', marginBottom: '1rem' }}>2026 Market Thesis</h2>
              <div style={{ background: 'var(--bg-secondary)', padding: '2rem', borderRadius: '16px', border: '1px solid var(--border-color)', borderLeft: '4px solid var(--accent-primary)' }}>
                <p style={{ color: 'var(--text-secondary)', lineHeight: '1.8', fontSize: '1.05rem', margin: 0 }}>{sol.marketThesis}</p>
              </div>
            </motion.div>

            {/* CTA */}
            <motion.div {...fadeUp(0.2)} style={{ textAlign: 'center', padding: '3rem 0' }}>
              <h3 style={{ fontSize: '1.5rem', fontWeight: '800', marginBottom: '1rem' }}>Ready to explore {sol.title}?</h3>
              <button onClick={() => navigate('/register')} style={{
                background: 'var(--accent-gradient)', padding: '1rem 2.5rem', borderRadius: '12px',
                color: '#000', fontWeight: '800', fontSize: '1rem', border: 'none', cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: '0.5rem', boxShadow: '0 8px 25px rgba(252,213,53,0.3)'
              }}>Start Investing <ArrowRight size={18} /></button>
            </motion.div>
          </div>
        )}

        {/* ===== STRATEGIES TAB ===== */}
        {activeTab === 'strategies' && (
          <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '3rem 5%' }}>
            <h2 style={{ fontSize: '1.8rem', fontWeight: '800', marginBottom: '1rem' }}>Investment Strategies</h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '3rem', fontSize: '1.05rem' }}>Alpha-generating approaches employed by our portfolio management team for {sol.title}.</p>
            {sol.strategies.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                {sol.strategies.map((s, i) => (
                  <motion.div key={i} {...fadeUp(i * 0.1)} whileHover={{ y: -3, boxShadow: '0 8px 25px rgba(0,0,0,0.1)' }} style={{ background: 'var(--bg-secondary)', padding: '2.5rem', borderRadius: '20px', border: '1px solid var(--border-color)', transition: 'border-color 0.3s' }}>
                    <h3 style={{ fontSize: '1.3rem', fontWeight: '800', marginBottom: '0.8rem', color: 'var(--accent-primary)' }}>{s.title}</h3>
                    <p style={{ color: 'var(--text-secondary)', lineHeight: '1.8', margin: 0 }}>{s.desc}</p>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div style={{ background: 'var(--bg-secondary)', padding: '3rem', borderRadius: '20px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
                <p style={{ color: 'var(--text-secondary)' }}>Detailed strategy content is being prepared by our research team. Check back soon.</p>
              </div>
            )}
          </div>
        )}

        {/* ===== RISK TAB ===== */}
        {activeTab === 'risk' && (
          <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '3rem 5%' }}>
            <h2 style={{ fontSize: '1.8rem', fontWeight: '800', marginBottom: '1rem' }}>Risk Management & Mitigation</h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '3rem', fontSize: '1.05rem' }}>Institutional-grade risk controls and hedging frameworks for {sol.title}.</p>
            {sol.risks.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                {sol.risks.map((r, i) => (
                  <motion.div key={i} {...fadeUp(i * 0.1)} whileHover={{ y: -3, boxShadow: '0 8px 25px rgba(0,0,0,0.1)' }} style={{ background: 'var(--bg-secondary)', padding: '2.5rem', borderRadius: '20px', border: '1px solid var(--border-color)' }}>
                    <h3 style={{ fontSize: '1.3rem', fontWeight: '800', marginBottom: '0.8rem' }}>{r.title}</h3>
                    <p style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '1rem' }}>{r.risk}</p>
                    <div style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: '12px', padding: '1rem 1.5rem' }}>
                      <p style={{ margin: 0, color: 'var(--success)', fontWeight: '700', fontSize: '0.85rem' }}>🛡️ Mitigation: <span style={{ fontWeight: '500', color: 'var(--text-secondary)' }}>{r.mitigation}</span></p>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div style={{ background: 'var(--bg-secondary)', padding: '3rem', borderRadius: '20px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
                <p style={{ color: 'var(--text-secondary)' }}>Detailed risk analysis is being prepared by our research team. Check back soon.</p>
              </div>
            )}
          </div>
        )}

        {/* ===== ADVANTAGE TAB ===== */}
        {activeTab === 'advantage' && (
          <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '3rem 5%' }}>
            <h2 style={{ fontSize: '1.8rem', fontWeight: '800', marginBottom: '1rem' }}>The Institutional Advantage</h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '3rem', fontSize: '1.05rem' }}>Exclusive access and capabilities that set our {sol.title} platform apart.</p>
            {sol.advantages.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
                {sol.advantages.map((a, i) => (
                  <motion.div key={i} {...fadeUp(i * 0.1)} whileHover={{ y: -4, scale: 1.02, boxShadow: '0 10px 30px rgba(0,0,0,0.12)' }} style={{ background: 'var(--bg-secondary)', padding: '2rem', borderRadius: '20px', border: '1px solid var(--border-color)' }}>
                    <div style={{ color: 'var(--accent-primary)', marginBottom: '1rem', fontSize: '1.5rem' }}>{a.icon || '⚡'}</div>
                    <h3 style={{ fontSize: '1.1rem', fontWeight: '800', marginBottom: '0.6rem' }}>{a.title}</h3>
                    <p style={{ color: 'var(--text-secondary)', lineHeight: '1.7', margin: 0, fontSize: '0.95rem' }}>{a.desc}</p>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div style={{ background: 'var(--bg-secondary)', padding: '3rem', borderRadius: '20px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
                <p style={{ color: 'var(--text-secondary)' }}>Institutional advantage details are being prepared. Check back soon.</p>
              </div>
            )}
          </div>
        )}

      </motion.div>
    </div>
  );
};

export default SolutionDetail;
